// VARIABLE (yield history) — derived from JD-Ops yield: combine selected crop-years into an
// expected-yield surface, then × book removal coefficients → lbs/ac. Replacement applies to P₂O₅
// and K₂O only (crop removal is a P/K concept; lime is a pH correction, not "removed").
import { BUNDLED_REFERENCE } from "./referenceData";

const cropRemovalDoc = BUNDLED_REFERENCE["crop-removal"];

export interface Replacement {
  p2o5: (number | null)[];
  k2o: (number | null)[];
}

// lbs of nutrient (as oxide) removed per BUSHEL of grain harvested — standard book values
// (TFI/IPNI "Nutrient Removal" / Tri-State). Editable in a later phase.
export const CROP_REMOVAL: Record<
  string,
  { p2o5: number; k2o: number; label: string }
> = { ...cropRemovalDoc.crops };

export interface CropRemovalDocument {
  crops: Record<string, { p2o5: number; k2o: number; label: string }>;
  default_yield: Record<string, number>;
  default_season_crops: string[];
}

const num = (v: number | null | undefined): v is number =>
  v != null && Number.isFinite(v);
const mean = (v: number[]) =>
  v.length ? v.reduce((s, x) => s + x, 0) / v.length : 0;
const fieldAvg = (v: (number | null)[]) => mean(v.filter(num));

export interface YieldLayer {
  crop: string;
  year: number;
  values: (number | null)[]; // per-cell yield (bu/ac)
}

export function combineYieldLayers(
  targetCrop: string,
  layers: YieldLayer[],
): (number | null)[] {
  const n = Math.max(0, ...layers.map((l) => l.values.length));
  const target = layers.filter((l) => l.crop === targetCrop);
  const others = layers.filter((l) => l.crop !== targetCrop);
  const out: (number | null)[] = new Array(n).fill(null);
  if (target.length === 0) return out;
  const otherFieldAvg = others.map((l) => fieldAvg(l.values));
  for (let i = 0; i < n; i++) {
    const tvals = target.map((l) => l.values[i]).filter(num);
    if (tvals.length === 0) continue; // no target-crop baseline at this cell
    const targetAvg = mean(tvals);
    let relSum = 0;
    let nOther = 0;
    for (let j = 0; j < others.length; j++) {
      const v = others[j].values[i];
      if (num(v) && otherFieldAvg[j] > 0) {
        relSum += v / otherFieldAvg[j];
        nOther++;
      }
    }
    const nTotal = tvals.length + nOther;
    out[i] = (targetAvg * (tvals.length + relSum)) / nTotal;
  }
  return out;
}

// expected-yield surface → replacement lbs/ac for the TARGET crop's removal coefficients
export function yieldToReplacement(
  combinedYield: (number | null)[],
  targetCrop: string,
): Replacement {
  const r = CROP_REMOVAL[targetCrop] ?? CROP_REMOVAL.corn;
  return {
    p2o5: combinedYield.map((v) => (num(v) ? Math.round(v * r.p2o5) : null)),
    k2o: combinedYield.map((v) => (num(v) ? Math.round(v * r.k2o) : null)),
  };
}

// static replacement — a flat FIELD-WIDE YIELD LEVEL (bu/ac) for the crop being replaced converted
// to product rates via the crop's removal coefficients static input is a yield level, not a rate).
export function flatYieldReplacement(
  cellCount: number,
  crop: string,
  yieldLevel: number,
): Replacement {
  const y = Math.max(0, yieldLevel) || 0;
  return yieldToReplacement(new Array(cellCount).fill(y), crop);
}
// typical starting yield levels (bu/ac) per crop for the static input's default
export const DEFAULT_YIELD: Record<string, number> = {
  ...cropRemovalDoc.default_yield,
};

// multi-season replacement — Build Rx presets A rotation plan: one entry per planned SEASON, each
// with its own crop + yield goal.
export interface Season {
  crop: string;
  yieldLevel: number;
}

// Sum a list of per-cell replacements element-wise. A cell counts a null contribution as 0, but a
// cell that is null in EVERY input stays null (no baseline anywhere → no rate, not a spurious 0).
export function sumReplacements(reps: Replacement[]): Replacement {
  const n = Math.max(
    0,
    ...reps.map((r) => Math.max(r.p2o5.length, r.k2o.length)),
  );
  const acc = (pick: (r: Replacement) => (number | null)[]) => {
    const out: (number | null)[] = new Array(n).fill(null);
    for (let i = 0; i < n; i++) {
      let sum = 0;
      let any = false;
      for (const r of reps) {
        const v = pick(r)[i];
        if (num(v)) {
          sum += v;
          any = true;
        }
      }
      out[i] = any ? Math.round(sum) : null;
    }
    return out;
  };
  return { p2o5: acc((r) => r.p2o5), k2o: acc((r) => r.k2o) };
}

// STATIC multi-season: each season is a flat field-wide yield goal → its crop's removal; summed.
export function multiSeasonStaticReplacement(
  cellCount: number,
  seasons: Season[],
): Replacement {
  return sumReplacements(
    seasons.map((s) => flatYieldReplacement(cellCount, s.crop, s.yieldLevel)),
  );
}

// ---- yield-history option discovery from the covariate catalog -------------------------
// Machine yield columns are named `mach_yield_<crop>_<year>` live (absolute year via the
// naming cutover; `<crop>` = corn/soybeans/…) or `mach_yield_<crop>_ym<N>` pre-cutover, and
// `machine__yield__<crop>__<year>` in mock. Detect them structurally: category 'machine' +
// a raw-yield key (not normalized/moisture/etc.), crop from the key, year from the CV1
// `year` field or parsed from the key (absolute, else `ym<N>` as a negative relative offset).
export interface YieldOption {
  key: string;
  crop: string;
  year: number; // absolute calendar year, or NEGATIVE = years-back (pre-cutover ym offset)
}
export function yieldOptionsFromCovariates(
  covs: {
    key: string;
    category?: string;
    has_values?: boolean;
    year?: number | null;
  }[],
): YieldOption[] {
  const out: YieldOption[] = [];
  for (const c of covs) {
    if (c.has_values === false || c.category !== "machine") continue;
    const key = (c.key ?? "").toLowerCase();
    if (!/(^|_)yield(_|$)/.test(key)) continue; // a raw yield column…
    if (/norm|moist|drymatter|wetmass|seed|appl/.test(key)) continue; // …not a derived one
    const crop = CROP_ALIASES.find(([re]) => re.test(key))?.[1];
    if (!crop) continue;
    let year = typeof c.year === "number" ? c.year : null;
    if (year == null) {
      const abs = /_(\d{4})(?:_|$)/.exec(key) ?? /(\d{4})$/.exec(key);
      const ym = /_ym(\d+)$/.exec(key);
      if (abs) year = Number(abs[1]);
      else if (ym) year = -Number(ym[1]); // relative "years back" — sorts newest-first, math is label-agnostic
    }
    if (year == null) continue;
    out.push({ key: c.key, crop, year });
  }
  // dedupe on key, newest first
  const seen = new Set<string>();
  return out
    .filter((o) => (seen.has(o.key) ? false : (seen.add(o.key), true)))
    .sort((a, b) => b.year - a.year || a.crop.localeCompare(b.crop));
}

// UI label for a yield option (absolute year, or "N yr ago" for a pre-cutover relative offset)
export function yieldOptionLabel(o: YieldOption): string {
  if (o.year > 0) return String(o.year);
  const n = -o.year;
  return `${n} yr${n === 1 ? "" : "s"} ago`;
}

// substring match (NOT \b — '_' is a word char, so \bcorn\b never fires on mach_yield_corn_…)
const CROP_ALIASES: [RegExp, string][] = [
  [/corn|maize/, "corn"],
  [/soybean/, "soybean"], // matches 'soybeans' too
  [/wheat/, "wheat"],
];

// yield SOURCES for the K customize workflow The editor offers two variable-rate bases: the
// grower's own MACHINE yield, and the platform's NDVI-SIMULATED yield for the crop, all years
// averaged.

/*
 *
 * Returns crop → column key, using the SAME `CROP_ALIASES` the machine path uses, so the two bases
 * can never disagree about what "soybean" is called.
 */
export function simYieldKeysByCrop(
  covs: { key: string; category?: string; has_values?: boolean }[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of covs) {
    if (c.has_values === false) continue;
    const key = (c.key ?? "").toLowerCase();
    // the all-years AVERAGE per crop — not the per-year columns, which carry a 4-digit year
    if (!/simyield/.test(key) || !/(^|_)avg(_|$)/.test(key)) continue;
    const crop = CROP_ALIASES.find(([re]) => re.test(key))?.[1];
    if (!crop || out[crop]) continue;
    out[crop] = c.key;
  }
  return out;
}

/* * Element-wise mean over several yield maps, skipping nulls. A cell null EVERYWHERE stays null. */
export function meanYieldMaps(maps: (number | null)[][]): (number | null)[] {
  const n = Math.max(0, ...maps.map((m) => m.length));
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    let k = 0;
    for (const m of maps) {
      const v = m[i];
      if (num(v)) {
        sum += v;
        k++;
      }
    }
    out[i] = k > 0 ? sum / k : null;
  }
  return out;
}

/*
 *
 * Crop removal from a per-cell YIELD map, scaled by the share of removal the grower wants to replace.
 * `removalPct` 80 means "put back 80% of what the crop takes" — the ask is literally a share of removal
 * so it scales the computed removal and nothing else (a flat typed rate is never touched by it).
 */
export function removalFromYield(
  yieldMap: (number | null)[],
  crop: string,
  removalPct = 100,
): Replacement {
  const r = CROP_REMOVAL[crop] ?? CROP_REMOVAL.corn;
  const f = (Number.isFinite(removalPct) ? removalPct : 100) / 100;
  return {
    p2o5: yieldMap.map((v) => (num(v) ? Math.round(v * r.p2o5 * f) : null)),
    k2o: yieldMap.map((v) => (num(v) ? Math.round(v * r.k2o * f) : null)),
  };
}

/* * A FLAT maintenance rate (lb/ac of the nutrient itself) as a Replacement — every cell the same. */
export function flatRateReplacement(
  cellCount: number,
  lbK2oPerSeason: number,
): Replacement {
  const v = Math.max(0, Number.isFinite(lbK2oPerSeason) ? lbK2oPerSeason : 0);
  return {
    p2o5: new Array(cellCount).fill(0),
    k2o: new Array(cellCount).fill(Math.round(v)),
  };
}

/* * A season the spread must carry: which crop, and which basis its maintenance comes from. */
export interface SeasonPlan {
  crop: string;
  basis: "flat" | "simulated" | "machine";
  /* * lb K₂O/ac for THIS season when its basis is flat; falls back to the per-crop preset constant */
  flatRateLbAc?: number;
}

export interface YieldMaps {
  machine: Record<string, (number | null)[]>;
  simulated: Record<string, (number | null)[]>;
}

/*
 *
 * PRESET FLAT RATES, per crop — derived, not typed in: the platform's own typical yield times its own
 * removal coefficient, so the flat fallback lands exactly where the variable path would at a typical
 * yield. Corn ≈ 220 × 0.27 = 59; soybean ≈ 80 × 1.4 = 112; wheat ≈ 80 × 0.3 = 24.
 */
const flatRates = (nutrient: "p2o5" | "k2o"): Record<string, number> =>
  Object.fromEntries(
    Object.keys(CROP_REMOVAL).map((crop) => [
      crop,
      Math.round(
        (DEFAULT_YIELD[crop] ?? 0) * (CROP_REMOVAL[crop]?.[nutrient] ?? 0),
      ),
    ]),
  );

const flatView = (nutrient: "p2o5" | "k2o"): Record<string, number> =>
  new Proxy({} as Record<string, number>, {
    get: (_target, prop) => flatRates(nutrient)[String(prop)],
    has: (_target, prop) => String(prop) in flatRates(nutrient),
    ownKeys: () => Object.keys(flatRates(nutrient)),
    getOwnPropertyDescriptor: (_target, prop) => {
      const table = flatRates(nutrient);
      if (!(String(prop) in table)) return undefined;
      return {
        enumerable: true,
        configurable: true,
        value: table[String(prop)],
      };
    },
  });

export const FLAT_K2O_BY_CROP: Record<string, number> = flatView("k2o");
/* * The P twin — corn ≈ 220 × 0.37 = 81; soybean ≈ 80 × 0.8 = 64; wheat ≈ 80 × 0.5 = 40. */
export const FLAT_P2O5_BY_CROP: Record<string, number> = flatView("p2o5");

/*
 *
 * The preset rotation: one corn season and one soybean season.
 */
export const DEFAULT_K_SEASON_CROPS: string[] = [
  ...cropRemovalDoc.default_season_crops,
];
export const DEFAULT_SEASON_CROPS = DEFAULT_K_SEASON_CROPS;

export function installCropRemoval(doc: CropRemovalDocument): void {
  for (const key of Object.keys(CROP_REMOVAL)) delete CROP_REMOVAL[key];
  Object.assign(CROP_REMOVAL, doc.crops);
  for (const key of Object.keys(DEFAULT_YIELD)) delete DEFAULT_YIELD[key];
  Object.assign(DEFAULT_YIELD, doc.default_yield);
  DEFAULT_K_SEASON_CROPS.splice(
    0,
    DEFAULT_K_SEASON_CROPS.length,
    ...doc.default_season_crops,
  );
}

/*
 *
 * Pick each season's basis by the DATA WE ACTUALLY HAVE for that crop:
 * machine yield → simulated yield → the per-crop flat constant.
 *
 * Per crop, not per field: a rotation can hold real machine soybean yield and only simulated corn, and the
 * chain resolves each season on its own merits rather than forcing one basis across both.
 */
export function resolveSeasons(
  crops: string[],
  maps: YieldMaps,
  nutrient: "p2o5" | "k2o" = "k2o",
): SeasonPlan[] {
  const flat = nutrient === "p2o5" ? FLAT_P2O5_BY_CROP : FLAT_K2O_BY_CROP;
  return crops.map((crop) => {
    if (maps.machine[crop]) return { crop, basis: "machine" as const };
    if (maps.simulated[crop]) return { crop, basis: "simulated" as const };
    return { crop, basis: "flat" as const, flatRateLbAc: flat[crop] ?? 0 };
  });
}
/* * @deprecated use `resolveSeasons` — kept so existing K call sites read unchanged */
export const resolveKSeasons = (
  crops: string[],
  maps: YieldMaps,
): SeasonPlan[] => resolveSeasons(crops, maps, "k2o");

/*
 *
 * Seasons → per-cell K maintenance. Pure; the caller has already fetched the maps.
 *
 * A season whose crop has no map on ITS OWN basis contributes NOTHING rather than a guessed yield — the
 * surfaces that can say so, do.
 */
export function replacementForSeasons(
  seasons: SeasonPlan[],
  maps: YieldMaps,
  cellCount: number,
  opts: {
    removalPct?: number;
    flatFallback?: number;
    nutrient?: "p2o5" | "k2o";
  } = {},
): Replacement | null {
  if (!seasons.length) return null;
  const nutrient = opts.nutrient ?? "k2o";
  const flatTable = nutrient === "p2o5" ? FLAT_P2O5_BY_CROP : FLAT_K2O_BY_CROP;
  const per = seasons.map((sn) => {
    if (sn.basis === "flat") {
      // a TYPED rate is never scaled by removalPct — it is already the number the grower chose. the
      // flat rate is in the CALLER's oxide: `flatRateReplacement` fills k2o, so the P caller reads
      // the p2o5 side below.
      const r = sn.flatRateLbAc ?? opts.flatFallback ?? flatTable[sn.crop] ?? 0;
      const flatR = flatRateReplacement(cellCount, r);
      return nutrient === "p2o5" ? { p2o5: flatR.k2o, k2o: flatR.p2o5 } : flatR;
    }
    const y = maps[sn.basis][sn.crop];
    return y
      ? removalFromYield(y, sn.crop, opts.removalPct ?? 100)
      : { p2o5: [], k2o: [] };
  });
  return sumReplacements(per);
}
